import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Sparkles, Tv } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { Order } from '@/components/KanbanCard'
import { supabase } from '@/integrations/supabase/client'
import { useCompany } from '@/lib/CompanyProvider'
import { useTenantFeature } from '@/hooks/use-tenant-feature'

export const Route = createFileRoute('/app/tv')({
  component: TVPanelPage,
})

const COLUMNS = [
  {
    id: 'AGUARDANDO',
    title: 'Fila de Espera',
    color: 'border-blue-500 text-blue-400 bg-blue-500/10',
  },
  {
    id: 'LAVANDO',
    title: 'Em Lavagem',
    color: 'border-yellow-500 text-yellow-400 bg-yellow-500/10',
  },
  {
    id: 'FINALIZANDO',
    title: 'Secando / Detalhamento',
    color: 'border-orange-500 text-orange-400 bg-orange-500/10',
  },
  {
    id: 'CONCLUIDO',
    title: 'Pronto para Entrega',
    color: 'border-emerald-500 text-emerald-400 bg-emerald-500/10',
  },
]

function maskPlate(plate: string) {
  if (!plate || plate.length < 5) return plate
  const clean = plate.replace('-', '')
  return `${clean.substring(0, 3)}-***${clean.substring(clean.length - 1)}`
}

function maskName(name: string) {
  if (!name) return ''
  const parts = name.trim().split(' ')
  if (parts.length === 1) return parts[0]
  return `${parts[0]} ${parts[parts.length - 1][0]}.`
}

function TVPanelPage() {
  const queryClient = useQueryClient()
  const { activeCompanyId, companies } = useCompany()
  const activeCompany = companies?.find((c) => c.id === activeCompanyId)
  const { hasAccess } = useTenantFeature('publicidade_tela')

  const [activeView, setActiveView] = useState<'KANBAN' | 'AD'>('KANBAN')
  const [activeAdIndex, setActiveAdIndex] = useState(0)

  // Buscar anúncios ativos (somente se tiver acesso)
  const { data: ads = [] } = useQuery({
    queryKey: ['tv_ads_active', activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return []
      const { data, error } = await supabase
        .from('tenant_midia_tv')
        .select('*')
        .eq('company_id', activeCompanyId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data
    },
    enabled: !!activeCompanyId && hasAccess,
  })

  const hasAds = ads.length > 0
  const activeAd = hasAds ? ads[activeAdIndex] : null

  // Timer de revezamento KANBAN <-> AD
  useEffect(() => {
    if (!hasAccess || !hasAds) {
      setActiveView('KANBAN')
      return
    }

    let timer: ReturnType<typeof setTimeout>

    if (activeView === 'KANBAN') {
      // Kanban fica 60s antes de mostrar o próximo anúncio
      timer = setTimeout(() => {
        setActiveView('AD')
      }, 60000)
    } else {
      // Estamos na view AD
      const isVideo = activeAd?.image_url.toLowerCase().endsWith('.mp4')
      if (!isVideo) {
        // Se for imagem, fica 15s antes de voltar
        timer = setTimeout(() => {
          setActiveView('KANBAN')
          setActiveAdIndex((prev) => (prev + 1) % ads.length)
        }, 15000)
      }
      // Se for vídeo, o onEnded fará a transição!
    }

    return () => clearTimeout(timer)
  }, [activeView, hasAccess, hasAds, ads.length, activeAd])

  const handleVideoEnded = () => {
    setActiveView('KANBAN')
    setActiveAdIndex((prev) => (prev + 1) % ads.length)
  }

  // Buscar ordens ativas (não pagas/finalizadas arquivadas)
  const { data: orders = [] } = useQuery({
    queryKey: ['tv_orders', activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return []
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('company_id', activeCompanyId)
        .neq('status', 'PAGO')
        .neq('status', 'ORCAMENTO')
        .order('created_at', { ascending: true })

      if (error) throw error
      return data as Order[]
    },
    enabled: !!activeCompanyId,
  })

  // Inscrição em Tempo Real (Supabase Realtime)
  useEffect(() => {
    if (!activeCompanyId) return
    const channel = supabase
      .channel('tv_orders_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => {
        queryClient.invalidateQueries({ queryKey: ['tv_orders', activeCompanyId] })
      })
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [activeCompanyId, queryClient])

  if (activeView === 'AD' && activeAd) {
    const isVideo = activeAd.image_url.toLowerCase().endsWith('.mp4')
    return (
      <div className="fixed inset-0 z-50 bg-black flex items-center justify-center overflow-hidden animate-in fade-in duration-1000">
        {isVideo ? (
          <video
            src={activeAd.image_url}
            autoPlay
            muted
            className="w-full h-full object-cover"
            onEnded={handleVideoEnded}
          />
        ) : (
          <img
            src={activeAd.image_url}
            alt={activeAd.title}
            className="w-full h-full object-cover"
          />
        )}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 text-slate-100 font-sans flex flex-col p-6 overflow-hidden select-none animate-in fade-in duration-1000">
      {/* Header do Painel da TV */}
      <div className="flex items-center justify-between pb-6 border-b border-slate-800/80">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <Tv className="text-white" size={28} />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-black text-white tracking-tight flex items-center gap-3">
              {activeCompany?.name || 'AutoWash Pro'}
              <span className="text-xs uppercase px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30 tracking-widest font-bold">
                Painel do Pátio
              </span>
            </h1>
            <p className="text-slate-400 text-sm mt-0.5">
              Acompanhe o status do seu veículo em tempo real
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Link
            to="/app"
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900/80 hover:bg-slate-800 border border-slate-800 text-slate-300 transition-colors"
          >
            <ArrowLeft size={16} />
            <span className="text-xs font-bold uppercase tracking-wider">Voltar</span>
          </Link>
          <div className="flex items-center gap-3 bg-slate-900/80 px-4 py-2 rounded-xl border border-slate-800">
            <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-mono font-bold text-slate-300 uppercase tracking-wider">
              Ao vivo na TV
            </span>
          </div>
        </div>
      </div>

      {/* Grid de Colunas do Pátio */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-6 overflow-hidden">
        {COLUMNS.map((col) => {
          const colOrders = orders.filter((o) => o.status === col.id)
          const isDoneCol = col.id === 'CONCLUIDO'

          return (
            <div
              key={col.id}
              className={`flex flex-col rounded-3xl border bg-slate-900/50 overflow-hidden ${
                isDoneCol ? 'border-emerald-500/40 bg-emerald-950/10' : 'border-slate-800/80'
              }`}
            >
              {/* Header da Coluna */}
              <div
                className={`p-4 border-b border-slate-800/80 flex items-center justify-between ${col.color}`}
              >
                <h2 className="font-bold text-lg tracking-wide flex items-center gap-2">
                  {col.title}
                </h2>
                <span className="w-8 h-8 rounded-full bg-slate-900/60 flex items-center justify-center font-extrabold text-sm border border-slate-700/50">
                  {colOrders.length}
                </span>
              </div>

              {/* Lista de Veículos na Coluna */}
              <div className="flex-1 p-4 space-y-4 overflow-y-auto hide-scrollbar">
                {colOrders.length === 0 ? (
                  <div className="h-40 flex items-center justify-center text-slate-600 text-sm font-medium italic">
                    Nenhum veículo
                  </div>
                ) : (
                  colOrders.map((order) => (
                    <div
                      key={order.id}
                      className={`p-5 rounded-2xl border transition-all duration-300 ${
                        isDoneCol
                          ? 'bg-emerald-900/20 border-emerald-500/50 shadow-lg shadow-emerald-500/10 animate-bounce-short'
                          : 'bg-slate-900 border-slate-800 hover:border-slate-700'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-2xl font-black tracking-wider text-white font-mono">
                          {maskPlate(order.plate)}
                        </span>
                        {isDoneCol ? (
                          <span className="flex items-center gap-1 text-xs font-bold bg-emerald-500 text-slate-950 px-2.5 py-1 rounded-full uppercase tracking-wider">
                            <Sparkles size={14} /> PRONTO!
                          </span>
                        ) : (
                          <span className="text-xs font-medium text-slate-400 bg-slate-800 px-2.5 py-1 rounded-md">
                            {order.vehicle_model}
                          </span>
                        )}
                      </div>

                      <div className="flex justify-between items-center text-sm pt-2 border-t border-slate-800/60">
                        <span className="text-slate-300 font-medium">
                          {maskName(order.customer_name)}
                        </span>
                        {isDoneCol && (
                          <span className="text-xs text-emerald-400 font-medium">
                            Disponível p/ retirada
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
